import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import { useFormikContext } from "formik";
import moment from "moment";
import usePermissions from "hooks/usePermissions";
import { useSelectedRows } from "common/contexts";
import { INITIAL_EVENT_DEFINITION } from "components/event-definitions/event-definition-form/useEventDefinitionCommonSteps";
export const MAX_SEARCH_WITHIN = 7 * 24 * 60 * 60 * 1e3;
export const MIN_EXECUTE_EVERY = 5 * 60 * 1e3;
export const SEARCH_WITHIN_UNITS = ["HOURS", "MINUTES", "DAYS"];
export const EXECUTE_EVERY_UNITS = ["HOURS", "MINUTES"];
export const DEFAULT_RULE_OPTIONS = {
  search_within: 5,
  search_within_unit: "MINUTES",
  execute_every: 5,
  execute_every_unit: "MINUTES",
  use_cron_scheduling: false,
  cron_expression: "",
  cron_timezone: "",
  streams: [],
  stream_categories: [],
  notifications: [],
  filters: [],
  remediation_steps: "",
  event_procedure_id: "",
  event_summary_template: "",
  enabled: true
};
export const INITIAL_IMPORT_ALL_OPTIONS = {
  ...DEFAULT_RULE_OPTIONS,
  enabled: false
};
export const INITIAL_SIGMA_EVENT_DEFINITION = {
  ...INITIAL_EVENT_DEFINITION,
  rule_ids: [],
  rule_overrides: void 0,
  overrides_all_valid: true,
  share_request: void 0
};
export const INITIAL_FILE_UPLOAD_EVENT_DEFINITION = {
  ...INITIAL_EVENT_DEFINITION,
  files: [],
  invalid_file_names: [],
  file_overrides: {},
  overrides_all_valid: true,
  share_request: void 0
};
export const PATH_ACTIONS = {
  gitImport: "git-import",
  fileUpload: "file-upload"
};
const validateRuleIds = (rule_ids) => {
  if (!rule_ids || rule_ids.length === 0) return "Please select at least one rule.";
  return null;
};
const validateFiles = (files) => {
  if (!files || files.length === 0) return "Please upload at least one file.";
  return null;
};
const validateStreams = (streams, streamsRequired) => {
  if (!streamsRequired) return null;
  if ((streams != null ? streams : []).length === 0) return "Please select at least one stream.";
  return null;
};
const validateSearchWithin = (time, unit) => {
  const ms = moment.duration(time, unit).asMilliseconds();
  if (ms > MAX_SEARCH_WITHIN) return "Max value is 7 DAYS";
  if (ms < 0) return "Invalid negative value";
  return null;
};
const validateExecuteEvery = (time, unit) => {
  const ms = moment.duration(time, unit).asMilliseconds();
  if (ms < MIN_EXECUTE_EVERY) return "Minimum value is 5 MINUTES";
  return null;
};
const validateOverrides = (overrides, ruleId, errors, pathAction, streamsRequired) => {
  const streamsError = validateStreams(overrides.streams, streamsRequired);
  const searchWithinError = validateSearchWithin(overrides.search_within, overrides.search_within_unit);
  let executeEveryError = null;
  if (!overrides.use_cron_scheduling)
    executeEveryError = validateExecuteEvery(overrides.execute_every, overrides.execute_every_unit);
  const overridesKey = pathAction === PATH_ACTIONS.gitImport ? "rule_overrides" : "file_overrides";
  if ((streamsError || searchWithinError || executeEveryError) && ruleId) {
    errors[overridesKey] = errors[overridesKey] || {};
    errors[overridesKey][ruleId] = {};
    if (streamsError) errors[overridesKey][ruleId].streams = streamsError;
    if (searchWithinError) errors[overridesKey][ruleId].search_within = searchWithinError;
    if (executeEveryError) errors[overridesKey][ruleId].execute_every = executeEveryError;
  }
};
export const validateGitImport = (values, streamsRequired = true) => {
  var _a;
  const newErrors = {};
  const ruleIdsError = validateRuleIds(values.rule_ids);
  if (ruleIdsError) newErrors.rule_ids = ruleIdsError;
  if (!ruleIdsError) {
    Object.entries((_a = values.rule_overrides) != null ? _a : {}).forEach(([ruleId, overrides]) => {
      validateOverrides(overrides, ruleId, newErrors, PATH_ACTIONS.gitImport, streamsRequired);
    });
  }
  return newErrors;
};
export const validateFileUpload = (values, streamsRequired = true) => {
  var _a;
  const newErrors = {};
  const filesError = validateFiles(values.files);
  if (filesError) newErrors.files = filesError;
  if (!filesError) {
    Object.entries((_a = values.file_overrides) != null ? _a : {}).forEach(([fileId, overrides]) => {
      validateOverrides(overrides, fileId, newErrors, PATH_ACTIONS.fileUpload, streamsRequired);
    });
  }
  return newErrors;
};
export const validateImportAll = (values, streamsRequired = true) => {
  const newErrors = {};
  const overrides = values.overrides;
  if (!overrides) return newErrors;
  const streamsError = validateStreams(overrides.streams, streamsRequired);
  const searchWithinError = validateSearchWithin(overrides.search_within, overrides.search_within_unit);
  let executeEveryError = null;
  if (!overrides.use_cron_scheduling)
    executeEveryError = validateExecuteEvery(overrides.execute_every, overrides.execute_every_unit);
  if (streamsError || searchWithinError || executeEveryError) {
    newErrors.overrides = {};
    if (streamsError) newErrors.overrides.streams = streamsError;
    if (searchWithinError) newErrors.overrides.search_within = searchWithinError;
    if (executeEveryError) newErrors.overrides.execute_every = executeEveryError;
  }
  return newErrors;
};
export const useValidations = (pathAction) => {
  const { isPermitted } = usePermissions();
  const streamsRequired = !isPermitted("streams:read");
  return useCallback(
    (values) => pathAction === PATH_ACTIONS.gitImport ? validateGitImport(values, streamsRequired) : validateFileUpload(values, streamsRequired),
    [pathAction, streamsRequired]
  );
};
const ruleOptionsToPayload = (options) => {
  const toMilliseconds = (time, unit) => {
    let milliseconds = time * 1e3;
    if (unit === "MINUTES" || unit === "HOURS" || unit === "DAYS") milliseconds *= 60;
    if (unit === "HOURS" || unit === "DAYS") milliseconds *= 60;
    if (unit === "DAYS") milliseconds *= 24;
    return milliseconds;
  };
  return {
    search_within_ms: toMilliseconds(options.search_within, options.search_within_unit),
    execute_every_ms: toMilliseconds(options.execute_every, options.execute_every_unit),
    use_cron_scheduling: options.use_cron_scheduling,
    cron_expression: options.cron_expression,
    cron_timezone: options.cron_timezone,
    streams: options.streams,
    stream_categories: options.stream_categories,
    notifications: options.notifications,
    filters: options.filters,
    event_procedure_id: options.event_procedure_id || void 0,
    event_summary_template: options.event_summary_template || null,
    enabled: options.enabled
  };
};
export const overridesMapToPayload = (overrides) => Object.fromEntries(Object.entries(overrides).map(([key, value]) => [key, ruleOptionsToPayload(value)]));
export const importAllOptionsToPayload = (options) => {
  const { event_summary_template, ...payload } = ruleOptionsToPayload(options);
  return payload;
};
export const fileOverridesToPayload = (overrides, files) => {
  const fileNameById = new Map((files != null ? files : []).map((file) => [file.name.split(".")[0], file.name]));
  return Object.fromEntries(
    Object.entries(overrides).map(([id, value]) => {
      var _a;
      return [(_a = fileNameById.get(id)) != null ? _a : id, ruleOptionsToPayload(value)];
    })
  );
};
export function FormikValidityMonitor({ onValidityChange }) {
  const { isValid } = useFormikContext();
  useEffect(() => {
    onValidityChange(isValid);
  }, [isValid, onValidityChange]);
  return null;
}
const readFileContent = (file, fileOverrides, invalidFileNames) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    var _a, _b;
    const fileId = file.name.split(".")[0];
    const content = reader.result;
    const title = ((_a = content.split("\n").find((line) => line.startsWith("title:"))) == null ? void 0 : _a.replace("title:", "").trim()) || file.name.split(".").slice(1).join(".");
    const description = ((_b = content.split("\n").find((line) => line.startsWith("description:"))) == null ? void 0 : _b.replace("description:", "").trim()) || "";
    resolve({
      name: title,
      description,
      overrides: (fileOverrides == null ? void 0 : fileOverrides[fileId]) || DEFAULT_RULE_OPTIONS,
      isInvalid: invalidFileNames.has(file.name)
    });
  };
  reader.onerror = () => reject(reader.error);
  reader.readAsText(file);
});
const getRulesSummaryData = async (pathAction, selectedRows, values, invalidFileNames) => {
  if (pathAction === PATH_ACTIONS.gitImport) {
    return Promise.resolve(
      selectedRows.map((rule) => {
        var _a;
        return {
          name: rule.title,
          description: rule.rule_description || "",
          overrides: ((_a = values.rule_overrides) == null ? void 0 : _a[rule.id]) || DEFAULT_RULE_OPTIONS
        };
      })
    );
  }
  return new Promise((resolve) => {
    var _a;
    const promises = Promise.all(
      ((_a = values.files) == null ? void 0 : _a.map((file) => readFileContent(file, values.file_overrides, invalidFileNames))) || []
    );
    promises.then((response) => resolve(response));
  });
};
export const useGetRulesSummaryData = (pathAction) => {
  var _a;
  const { values } = useFormikContext();
  const selectedRows = useSelectedRows();
  const invalidFileNames = useMemo(() => {
    var _a2;
    return new Set((_a2 = values.invalid_file_names) != null ? _a2 : []);
  }, [values.invalid_file_names]);
  const { data, isLoading } = useQuery({
    queryKey: ["summary-rule-data", pathAction, selectedRows, values, invalidFileNames],
    queryFn: () => getRulesSummaryData(pathAction, selectedRows, values, invalidFileNames),
    retry: 1,
    enabled: selectedRows.length > 0 || ((_a = values.files) == null ? void 0 : _a.length) > 0
  });
  return {
    loadingSummaryData: isLoading,
    summaryRulesData: data || []
  };
};
